import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PlayCircle, BookOpen, HelpCircle } from 'lucide-react';

// Centralized URL configuration for easy updates
export const HELP_URLS = {
  snapshotAnalysis: {
    youtube: 'https://youtu.be/9o_4TDWApeM',  // Official how-to video
    benefits: '/snapshot/benefits',  // Internal route
  },
  sfrCalculator: {
    youtube: 'https://youtu.be/R7TeMLahoXU',  // Official how-to video
    benefits: '/sfr-calculator/benefits',  // Internal route
  },
  growthModelAnalysis: {
    youtube: 'https://youtu.be/xChm3Hxm5lI',  // Official how-to video
    benefits: '/growth-model/benefits',  // Internal route
  },
  growthPrognosis: {
    youtube: 'https://youtu.be/BkY9MLpjop4',  // Official how-to video
    benefits: '/growth-prognosis/benefits',  // Internal route
  },
  spreadsheetComparison: {
    youtube: 'https://youtu.be/0eeXMiNyUHQ',  // Official how-to video
    benefits: '/spreadsheet-comparison/benefits',  // Internal route
  },
  customModelUpload: {
    youtube: 'https://youtu.be/aXQ9JEGTID4',  // Official how-to video
    benefits: '/custom-model/benefits',  // Internal route
  },
  temperatureModelUpload: {
    youtube: 'https://youtu.be/3qTPzke_6xk',  // Official how-to video
    benefits: '/temperature-model/benefits',  // Internal route
  },
  multiProfileComparison: {
    youtube: 'https://youtu.be/V6H_mjA5zNQ',
    benefits: '/multi-profile/benefits',  // Internal route
  },
};

export default function HelpBenefits({ 
  youtubeUrl = 'YOUTUBE_URL_PLACEHOLDER', 
  benefitsUrl = 'BENEFITS_URL_PLACEHOLDER',
  featureName = 'this feature'
}) {
  const navigate = useNavigate();
  const isYoutubePlaceholder = youtubeUrl === 'YOUTUBE_URL_PLACEHOLDER';
  const isBenefitsPlaceholder = benefitsUrl === 'BENEFITS_URL_PLACEHOLDER';
  const isInternalBenefitsLink = benefitsUrl.startsWith('/');

  const handleBenefitsClick = (e) => {
    if (isBenefitsPlaceholder) {
      e.preventDefault();
      return;
    }
    if (isInternalBenefitsLink) {
      e.preventDefault();
      navigate(benefitsUrl);
    }
    // External links will open normally in new tab
  };

  return (
    <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-white" data-testid="help-benefits-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-[#0891b2] text-lg">
          <HelpCircle className="h-5 w-5" />
          Help & Benefits
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* How-to Video Link */}
        <a
          href={isYoutubePlaceholder ? '#' : youtubeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
            isYoutubePlaceholder 
              ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' 
              : 'border-red-100 bg-red-50 hover:bg-red-100 hover:border-red-200 text-red-700 hover:text-red-800'
          }`}
          onClick={(e) => isYoutubePlaceholder && e.preventDefault()}
          data-testid="help-youtube-link"
        >
          <div className={`p-2 rounded-full ${isYoutubePlaceholder ? 'bg-gray-200' : 'bg-red-100'}`}>
            <PlayCircle className={`h-5 w-5 ${isYoutubePlaceholder ? 'text-gray-400' : 'text-red-600'}`} />
          </div>
          <div>
            <p className="font-medium text-sm">Watch: How to use {featureName}</p>
            <p className={`text-xs ${isYoutubePlaceholder ? 'text-gray-400' : 'text-red-500'}`}>
              {isYoutubePlaceholder ? 'Video coming soon' : 'Opens in YouTube'}
            </p>
          </div>
        </a>

        {/* Benefits Link */}
        <a
          href={isBenefitsPlaceholder ? '#' : benefitsUrl}
          target={isInternalBenefitsLink ? '_self' : '_blank'}
          rel="noopener noreferrer"
          className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
            isBenefitsPlaceholder 
              ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' 
              : 'border-[#0891b2]/20 bg-[#0891b2]/5 hover:bg-[#0891b2]/10 hover:border-[#0891b2]/30 text-[#0891b2] hover:text-[#0e7490]'
          }`}
          onClick={handleBenefitsClick}
          data-testid="help-benefits-link"
        >
          <div className={`p-2 rounded-full ${isBenefitsPlaceholder ? 'bg-gray-200' : 'bg-[#0891b2]/10'}`}>
            <BookOpen className={`h-5 w-5 ${isBenefitsPlaceholder ? 'text-gray-400' : 'text-[#0891b2]'}`} />
          </div>
          <div>
            <p className="font-medium text-sm">Learn: Benefits of {featureName}</p>
            <p className={`text-xs ${isBenefitsPlaceholder ? 'text-gray-400' : 'text-[#0891b2]/70'}`}>
              {isBenefitsPlaceholder ? 'Guide coming soon' : (isInternalBenefitsLink ? 'View benefits guide' : 'Opens in new tab')}
            </p>
          </div>
        </a>
      </CardContent>
    </Card>
  );
}
